import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useEffect, useState } from 'react';
import { isAfter, isBefore, startOfDay } from 'date-fns';
import { isEqual } from 'lodash-es';
import { FilterPicker, RadioButtonWithLabel } from 'seagull';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { formatNumericUSDate } from '~/utils';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
const DateRangePickerFilter = (param)=>{
    let { value, removable, startOpen, dateLimits, initialDate, onChange, onRemove, onInputBlur, onInputFocus, testId, label } = param;
    const { t: texts } = useLocaleKeys('components.dateRangeFilterPicker');
    const { getNow } = useIoContext();
    const hasDatesFromProps = !!((value === null || value === void 0 ? void 0 : value.from) && (value === null || value === void 0 ? void 0 : value.to));
    const [selectingRange, setSelectingRange] = useState(hasDatesFromProps);
    const [from, setFrom] = useState(getInitialDate('from'));
    const [to, setTo] = useState(getInitialDate('to'));
    useEffect(()=>{
        value.from && setFrom(new Date(value.from));
    }, [
        value.from
    ]);
    useEffect(()=>{
        value.to && setTo(new Date(value.to));
    }, [
        value.to
    ]);
    const labelToDisplay = useMemo(()=>{
        const { from, to } = value;
        return from && to ? "".concat(label, " (").concat(from, "-").concat(to, ")") : label;
    }, [
        label,
        value
    ]);
    function getInitialDate(type) {
        const dateFromInitialDate = initialDate === null || initialDate === void 0 ? void 0 : initialDate[type];
        const dateFromValue = value === null || value === void 0 ? void 0 : value[type];
        return dateFromValue ? new Date(dateFromValue) : dateFromInitialDate;
    }
    function onChangeIfDifferentValue(newValue) {
        if (!isEqual(newValue, value)) onChange(newValue);
    }
    function shouldDisableApply() {
        if (selectingRange && from && to) {
            const fromDay = startOfDay(from);
            const toDay = startOfDay(to);
            const { min, max } = dateLimits || {};
            const minDay = min ? startOfDay(min) : undefined;
            const maxDay = max ? startOfDay(max) : undefined;
            return isAfter(fromDay, toDay) || minDay && isBefore(fromDay, minDay) || maxDay && isAfter(toDay, maxDay);
        }
        return false;
    }
    return /*#__PURE__*/ _jsxs(FilterPicker, {
        removable: removable,
        startOpen: startOpen,
        testId: testId,
        disableApply: shouldDisableApply(),
        label: labelToDisplay,
        title: label,
        highlightTrigger: !!(value.from && value.to),
        onRemove: onRemove,
        onApply: ()=>onChangeIfDifferentValue(selectingRange ? {
                from: from != null ? formatNumericUSDate(from) : undefined,
                to: to != null ? formatNumericUSDate(to) : undefined
            } : {}),
        onClose: (opts)=>{
            if (opts === null || opts === void 0 ? void 0 : opts.revert) {
                setSelectingRange(hasDatesFromProps);
                setFrom(getInitialDate('from'));
                setTo(getInitialDate('to'));
            }
        },
        children: [
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'date_range_all'
                },
                label: texts.selectAll(),
                testId: "".concat(testId, "-filter-all"),
                checked: !selectingRange,
                onChange: ()=>{
                    setSelectingRange(false);
                }
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'date_range_specific'
                },
                label: texts.specificRange(),
                testId: "".concat(testId, "-filter-range"),
                checked: selectingRange,
                onChange: ()=>{
                    setSelectingRange(true);
                    const now = startOfDay(getNow());
                    if (!from) setFrom(now);
                    if (!to) setTo(now);
                }
            }),
            selectingRange && from && to && /*#__PURE__*/ _jsx(DateRangePicker, {
                testId: testId,
                fromDate: from,
                toDate: to,
                dateLimits: dateLimits,
                onChange: (data)=>{
                    setFrom(data.fromDate);
                    setTo(data.toDate);
                },
                onInputFocus: onInputFocus,
                onInputBlur: onInputBlur
            })
        ]
    });
};
export default DateRangePickerFilter;
